import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { Album, AlbumCard } from '@photos/shared';
import { useAlbums, useCreateAlbum } from '../../api/hooks';
import { Icon } from '../../components/Icon';
import { Modal } from '../../components/Modal';
import { TagEditor } from '../../components/TagEditor';
import { avatarColor, fmtCount, fmtRelative, initials } from '../../lib/format';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';

type Sort = 'activity' | 'name' | 'created';

/** "84 unreleased · 230 released · 6 restricted" (restricted only for people who may see it). */
export function albumCounts(a: Pick<AlbumCard, 'counts'>, seesRestricted: boolean): string {
  const parts = [`${fmtCount(a.counts.unreleased)} unreleased`, `${fmtCount(a.counts.released)} released`];
  if (seesRestricted) parts.push(`${fmtCount(a.counts.restricted)} restricted`);
  return parts.join(' · ');
}

export function Avatars({ people }: { people: { userId: string; name: string }[] }) {
  if (!people.length) return null;
  return (
    <span className="avs" title={people.map((p) => p.name).join(', ')}>
      {people.slice(0, 4).map((p) => (
        <span key={p.userId} className="av" style={{ background: avatarColor(p.name) }}>
          {initials(p.name)}
        </span>
      ))}
      {people.length > 4 && <span className="av more">+{people.length - 4}</span>}
    </span>
  );
}

interface NewAlbumDialogProps {
  onClose(): void;
  /** Upload's "+ New album": the typed name, and the created album goes back to the upload instead of opening. */
  initialName?: string;
  onCreated?(album: Album): void;
}

/** New album: name, optional description and default tags (every upload into the album starts with them). */
export function NewAlbumDialog({ onClose, initialName = '', onCreated }: NewAlbumDialogProps) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState('');
  const [defaultTags, setDefaultTags] = useState<string[]>([]);
  const create = useCreateAlbum();
  const navigate = useNavigate();
  const toast = useToast();
  const inline = !!onCreated;
  const save = (tags: string[]) =>
    create.mutate(
      { name: name.trim(), description: description.trim(), defaultTags: tags },
      {
        onSuccess: (a) => {
          onClose();
          toast({ icon: 'folder', title: `Created ${a.name}` });
          if (onCreated) onCreated(a);
          else navigate(`/studio/albums/${a.id}`);
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t create the album', sub: e.message }),
      },
    );
  return (
    <Modal onClose={onClose} labelledBy="new-album-title">
      <h3 id="new-album-title">{inline ? `New album: ${initialName}` : 'New album'}</h3>
      <p className="help">
        {inline
          ? 'Pick tags every upload into this album should start with (you can remove them per upload), or skip.'
          : 'Albums organize photos in Studio. Releasing decides who can see them.'}
      </p>
      {!inline && (
        <>
          <div className="field">
            <label htmlFor="na-name">Name</label>
            <input id="na-name" className="inp" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer Retreat 2026" />
          </div>
          <div className="field">
            <label htmlFor="na-desc">
              Description <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</small>
            </label>
            <input id="na-desc" className="inp" maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </>
      )}
      <div className="field">
        <label>
          Album tags <small style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional · e.g. the event and region)</small>
        </label>
        <TagEditor id="na-tags" tags={defaultTags} onChange={setDefaultTags} placeholder="Add tag… (e.g. summer-retreat)" />
      </div>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        {inline && (
          <button type="button" className="btn" disabled={create.isPending} onClick={() => save([])}>
            Skip
          </button>
        )}
        <button type="button" className="btn primary" disabled={!name.trim() || create.isPending} onClick={() => save(defaultTags)}>
          Create album
        </button>
      </div>
    </Modal>
  );
}

/** `/studio/albums`: album cards with filters (mine / all), search and sort. */
export function AlbumsPage() {
  const { access } = useSession();
  const albums = useAlbums();
  const navigate = useNavigate();
  const [scope, setScope] = useState<'mine' | 'all'>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<Sort>('activity');
  const [creating, setCreating] = useState(false);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = (albums.data?.items ?? []).filter((a) => (scope === 'all' || a.mine) && (!q || a.name.toLowerCase().includes(q)));
    const by: Record<Sort, (a: AlbumCard, b: AlbumCard) => number> = {
      activity: (a, b) => (b.lastActivity?.at ?? b.updatedAt).localeCompare(a.lastActivity?.at ?? a.updatedAt),
      name: (a, b) => a.name.localeCompare(b.name),
      created: (a, b) => b.createdAt.localeCompare(a.createdAt),
    };
    return items.sort(by[sort]);
  }, [albums.data, scope, query, sort]);

  return (
    <>
      <div className="toolbar">
        <h1>Albums</h1>
        <span className="meta">{albums.data ? `${fmtCount(albums.data.items.length)} albums` : ''}</span>
        <span style={{ flex: 1 }} />
        {access.isMedia && (
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            <Icon name="plus" />
            New album
          </button>
        )}
      </div>
      <div className="stoolbar">
        <div className="subtabs" role="radiogroup" aria-label="Which albums">
          {(['all', 'mine'] as const).map((k) => (
            <button type="button" key={k} role="radio" aria-checked={scope === k} className={scope === k ? 'on' : ''} onClick={() => setScope(k)}>
              {k === 'all' ? 'All albums' : 'My albums'}
            </button>
          ))}
        </div>
        <input className="inp sm" type="search" placeholder="Search albums…" aria-label="Search albums" value={query} onChange={(e) => setQuery(e.target.value)} />
        <select className="inp sm" aria-label="Sort albums" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
          <option value="activity">Recent activity</option>
          <option value="name">Name</option>
          <option value="created">Date created</option>
        </select>
      </div>
      {albums.isError ? (
        <div className="empty">
          <h3>Couldn’t load albums</h3>
          {albums.error.message}
        </div>
      ) : !albums.data ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : !list.length ? (
        <div className="empty">
          <h3>No albums</h3>
          {query ? 'No album matches that name.' : scope === 'mine' ? 'Albums you created, joined or uploaded to show up here.' : 'Create an album to start uploading.'}
        </div>
      ) : (
        <div className="cards">
          {list.map((a) => (
            <button type="button" className="card album-card" key={a.id} onClick={() => navigate(`/studio/albums/${a.id}`)}>
              <div className="album-cover">{a.coverUrl ? <img alt="" loading="lazy" src={a.coverUrl} /> : <Icon name="folder" />}</div>
              <div className="body">
                <div className="t">{a.name}</div>
                <div className="s counts">
                  <span data-vis="unreleased">{fmtCount(a.counts.unreleased)} unreleased</span>
                  <span data-vis="released">{fmtCount(a.counts.released)} released</span>
                  {access.seesRestricted && <span data-vis="restricted">{fmtCount(a.counts.restricted)} restricted</span>}
                </div>
                <div className="s last">
                  {a.lastActivity ? (
                    <span title={a.lastActivity.summary}>
                      {a.lastActivity.summary} · {fmtRelative(a.lastActivity.at)}
                    </span>
                  ) : (
                    <span>Created by {a.createdByName}</span>
                  )}
                </div>
                <Avatars people={a.collaborators} />
              </div>
            </button>
          ))}
        </div>
      )}
      {creating && <NewAlbumDialog onClose={() => setCreating(false)} />}
    </>
  );
}
